import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { UnsavedChangesDialogComponent } from '../../Dialogs/unsaved-changes-dialog/unsaved-changes-dialog.component';
import { UnsavedChangesTrackerService } from './unsaved-changes-tracker.service';

/**
 * Shared leave-protection helpers used by host pages.
 * Browser Back with unsaved edits is handled by UnsavedChangesBrowserBackService;
 * this helper covers in-app route leave (canDeactivate) and beforeunload.
 */
export class UnsavedChangesHostHelper {
  private static activeConfirm: Promise<boolean> | null = null;
  private static activeDialogRef: MatDialogRef<
    UnsavedChangesDialogComponent,
    boolean
  > | null = null;

  static get isConfirmOpen(): boolean {
    return UnsavedChangesHostHelper.activeDialogRef != null;
  }

  constructor(
    private readonly tracker: UnsavedChangesTrackerService,
    private readonly dialog?: MatDialog | null,
    private readonly router?: Router | null
  ) {}

  canDeactivate(): Promise<boolean> {
    if (!this.tracker.hasUnsavedChanges()) {
      return Promise.resolve(true);
    }
    if (!this.dialog) {
      return Promise.resolve(false);
    }

    const stayUrl = this.router?.url ?? null;

    return UnsavedChangesHostHelper.openConfirm(this.dialog).then((discard) => {
      if (discard) {
        this.discard();
        return true;
      }
      if (stayUrl && this.router && this.router.url !== stayUrl) {
        void this.router.navigateByUrl(stayUrl, { replaceUrl: true });
      }
      return false;
    });
  }

  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.tracker.hasUnsavedChanges()) {
      return;
    }
    // Native prompt only for Refresh/Close. Skip while our custom confirm is open.
    if (UnsavedChangesHostHelper.isConfirmOpen) {
      return;
    }
    event.preventDefault();
    event.returnValue = '';
  }

  discard(): void {
    this.dialog?.closeAll();
    this.tracker.requestDiscard();
  }

  clearOnDestroy(): void {
    this.tracker.clear();
  }

  /**
   * Opens the Unsaved Changes dialog (singleton while open).
   * @returns `true` for Discard Changes, `false` for Stay on Page.
   */
  static openConfirm(dialog: MatDialog): Promise<boolean> {
    if (UnsavedChangesHostHelper.activeConfirm) {
      return UnsavedChangesHostHelper.activeConfirm;
    }

    const ref = dialog.open<UnsavedChangesDialogComponent, void, boolean>(
      UnsavedChangesDialogComponent,
      {
        width: '520px',
        disableClose: true,
        autoFocus: false,
        closeOnNavigation: false,
      }
    );
    UnsavedChangesHostHelper.activeDialogRef = ref;

    UnsavedChangesHostHelper.activeConfirm = firstValueFrom(ref.afterClosed())
      .then((result) => result === true)
      .finally(() => {
        UnsavedChangesHostHelper.activeConfirm = null;
        UnsavedChangesHostHelper.activeDialogRef = null;
      });

    return UnsavedChangesHostHelper.activeConfirm;
  }
}
